/**
 * User reducer
 * TODO: change this to guest-user
 */

import _ from 'lodash'
import { ActionTypes as types, GOVT_ID_TYPES } from '../constants'
import serverProps from '../utils/serverProps'
import { getExamplePhoneNumber } from '../utils/phoneUtils'

function getInitialState(props) {
  const {
    locality = {},
    country = '',
    language = '',
    addressEntryFormat = {},
    requestee = {},
    addressBook = [],
    addresses = [],
    primaryCurrencyCode = '',
    guestSupportedCountries = [],
    moneyPoolsCount = 0,
    user: { timeZone = '', subscriptionType = '' } = {},
    firstName = _.get(props, 'user.firstName', ''),
    lastName = _.get(props, 'user.lastName', ''),
    fullName = _.get(props, 'user.fullName', ''),
    isPersonalDisabled = _.get(props, 'isPersonalDisabled', null),
    isPurchaseDisabled = _.get(props, 'isPurchaseDisabled', null),
    personalCipUrl = _.get(props, 'personalCipUrl', null),
    purchaseCipUrl = _.get(props, 'purchaseCipUrl', null),
    mgmChecklistStatus = {},
    transactionStory = {},
    requesterCurrencies = _.get(props, 'requesterCurrencies', null),
    isRequesterCurrencyRestricted = _.get(
      props,
      'isRequesterCurrencyRestricted',
      null
    ),
    isNoteRestricted = _.get(props, 'isNoteRestricted', false),
    purchaseConsentRequired = _.get(props, 'purchaseConsentRequired', false),
    purchaseConsentLabel = _.get(props, 'purchaseConsentLabel', ''),
    purchaseConsentChecked = _.get(props, 'purchaseConsentChecked', false),
    userAgreementConsentRequired = _.get(
      props,
      'userAgreementConsentRequired',
      false
    ),
    userAgreementConsentLabel = _.get(props, 'userAgreementConsentLabel', ''),
    userAgreementConsentMinVersion = _.get(
      props,
      'userAgreementConsentMinVersion',
      ''
    ),
    userAgreementConsentMaxVersion = _.get(
      props,
      'userAgreementConsentMaxVersion',
      ''
    ),
    hasResolvedUserAgreementConsent = false,
    requireSenderInstrumentAddition = _.get(
      props,
      'recipientData.requireSenderInstrumentAddition',
      false
    ),
  } = props

  const { id = '', type } = requestee
  const requesteeIsEmail = type === 'EMAIL' || id.includes('@') // TODO: pull in the email validators we have in some utils
  const email = requesteeIsEmail ? id : ''
  const phoneNumber = requesteeIsEmail ? '' : id
  const initialCountry = country || locality.country
  const initialLanguage = language || locality.language

  return {
    payerId: '',
    country: initialCountry,
    language: initialLanguage,
    primaryCurrencyCode,
    addressEntryFormat,
    requestee,
    email,
    phoneNumber,
    examplePhoneNumber: '',
    firstName,
    lastName,
    fullName,
    newBillingAddress: {},
    newShippingAddress: {},
    newTravelRuleAddress: {},
    addresses,
    selectedAddress:
      addresses.find(address => !!address.isDefault) || addresses[0],
    addressBook, // TODO: rename/merge with addresses
    password: '',
    isFullAccount: false,
    accountWasUpgraded: false,
    selectedGovernmentIdType: GOVT_ID_TYPES.SSN,
    governmentId: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    showRecipientAccountInfo: false,
    countryCanGuest: guestSupportedCountries.includes(initialCountry),
    moneyPoolsCount,
    isUpgradeEligible: false,
    timeZone: timeZone || locality.timeZone || '',
    subscriptionType: subscriptionType || '',
    instrumentationData: {
      purchaseProtection: {
        changedPurchaseProtection: false,
      },
    },
    proposePhoto: '',
    isPersonalDisabled: isPersonalDisabled,
    isPurchaseDisabled: isPurchaseDisabled,
    personalCipUrl: personalCipUrl,
    purchaseCipUrl: purchaseCipUrl,
    mgmChecklistStatus,
    transactionStory,
    triggerSenderPhoneConfirmation: false,
    requesterCurrencies,
    isRequesterCurrencyRestricted,
    isNoteRestricted,
    purchaseConsentRequired,
    purchaseConsentLabel,
    purchaseConsentChecked,
    userAgreementConsentRequired,
    userAgreementConsentLabel,
    userAgreementConsentMinVersion,
    userAgreementConsentMaxVersion,
    hasResolvedUserAgreementConsent,
    requireSenderInstrumentAddition,
  }
}

const initialState = getInitialState(serverProps)

/**
 * User's address specific reducer
 */
const addressReducer = (state, action) => {
  let isFullAccount = false

  switch (action.type) {
    case types.USER.CHANGE_BILLING_ADDRESS_ENTRY_FIELD:
      const newBillingAddress = {
        ...state.newBillingAddress,
        [action.data.field]: action.data.value,
      }
      return {
        ...state,
        newBillingAddress,
      }

    case types.USER.CHANGE_SHIPPING_ADDRESS_ENTRY_FIELD:
      const newShippingAddress = {
        ...state.newShippingAddress,
        [action.data.field]: action.data.value,
      }
      return {
        ...state,
        newShippingAddress,
      }

    case types.TRANSACTION.INTENDS_TO_ADD_SHIPPING_ADDRESS:
      // if false (user doesn't intend), clear any data we may have stored
      return {
        ...state,
        newShippingAddress: !action.data.doesIntend
          ? {}
          : state.newShippingAddress,
      }

    case types.USER.CHANGE_TRAVEL_RULE_ADDRESS_ENTRY_FIELD:
      const newTravelRuleAddress = {
        ...state.newTravelRuleAddress,
        [action.data.field]: action.data.value,
      }
      return {
        ...state,
        newTravelRuleAddress,
      }
    // if a full account was created, set `isFullAccount` and fall through
    case types.USER.CREATED_FULL_ACCOUNT:
      isFullAccount = !!action.data.address.id
    // intentionally fall through

    case types.USER.CREATED_TEMP_ACCOUNT:
      const { payerId, address = {} } = action.data
      const newAddressBook = [...state.addressBook, address]
      return {
        ...state,
        payerId,
        addressBook: newAddressBook,
        isFullAccount,
      }

    default:
      return state
  }
}

export default (state = initialState, action) => {
  switch (action.type) {
    case types.EXTERNAL.RECEIVED_DATA:
      return {
        ...state,
        ...getInitialState(action.data),
      }

    case types.TRANSACTION.PURCHASE_CONSENT_IS_CHECKED:
      return {
        ...state,
        purchaseConsentChecked: action.data.purchaseConsentChecked,
      }

    case types.TRANSACTION.RESOLVE_USER_AGREEMENT_CONSENT:
      return {
        ...state,
        hasResolvedUserAgreementConsent: true,
      }
    case types.USER.CHANGE_COUNTRY:
      return {
        ...state,
        country: action.data.country,
        examplePhoneNumber:
          (action.data.countryCanGuest &&
            getExamplePhoneNumber(action.data.country)) ||
          state.examplePhoneNumber,
        countryCanGuest: action.data.countryCanGuest,
      }

    case types.TRANSACTION.RECEIVED_SPECS:
      return {
        ...state,
        addressEntryFormat: action.data.addressEntryFormat,
      }

    case types.USER.CHANGE_PRIMARY_CURRENCY_CODE:
      return {
        ...state,
        primaryCurrencyCode: action.data.primaryCurrencyCode,
      }

    case types.USER.CHANGE_FIRST_NAME:
      return {
        ...state,
        firstName: action.data.firstName,
      }

    case types.USER.CHANGE_LAST_NAME:
      return {
        ...state,
        lastName: action.data.lastName,
      }

    case types.USER.CHANGE_EMAIL:
      return {
        ...state,
        email: action.data.email,
      }

    case types.USER.CHANGE_PHONE_NUMBER:
      return {
        ...state,
        phoneNumber: action.data.phoneNumber,
      }

    case types.TRANSACTION.LEAVING_SUCCESS_PAGE:
    case types.TRANSACTION.CANCELLED_TRANSACTION:
      return {
        ...state,
        requireSenderInstrumentAddition: false,
      }

    case types.USER.CHANGE_BILLING_ADDRESS_ENTRY_FIELD:
    case types.TRANSACTION.INTENDS_TO_ADD_SHIPPING_ADDRESS:
    case types.USER.CHANGE_SHIPPING_ADDRESS_ENTRY_FIELD:
    case types.USER.CHANGE_TRAVEL_RULE_ADDRESS_ENTRY_FIELD:
    case types.USER.CREATED_FULL_ACCOUNT:
    case types.USER.CREATED_TEMP_ACCOUNT:
      // maybe we wanna just put this in the default case
      return addressReducer(state, action)

    case types.USER.SET_PASSWORD:
      return {
        ...state,
        password: action.data.password,
      }

    case types.USER.UPGRADED_ACCOUNT:
      return {
        ...state,
        accountWasUpgraded: action.data.accountWasUpgraded,
      }

    case types.USER.CHANGE_GOVT_ID:
      return {
        ...state,
        governmentId: action.data.governmentId,
      }

    case types.USER.CHANGE_SELECTED_GOVERNMENT_ID_TYPE:
      return {
        ...state,
        selectedGovernmentIdType: action.data.selectedGovernmentIdType,
        governmentId: '',
      }

    case types.USER.CHANGE_BIRTH_MONTH:
      return {
        ...state,
        birthMonth: action.data.birthMonth,
      }

    case types.USER.CHANGE_BIRTH_DAY:
      return {
        ...state,
        birthDay: action.data.birthDay,
      }

    case types.USER.CHANGE_BIRTH_YEAR:
      return {
        ...state,
        birthYear: action.data.birthYear,
      }
    // used for guest p2p, indicates whether a user can upgrade to full account at end of flow
    case types.USER.RECEIVED_UPGRADE_ELIGIBILITY:
      return {
        ...state,
        isUpgradeEligible: action.data.isUpgradeEligible,
      }

    case types.USER.TOGGLE_SENDER_HELPER_INFORMATION:
      return {
        ...state,
        showRecipientAccountInfo: !state.showRecipientAccountInfo,
      }

    case types.INSTRUMENTATION.PURCHASE_PROTECTION:
      const newInstrumentationData = {
        ...state.instrumentationData.purchaseProtection,
        ...action.data,
      }

      return {
        ...state,
        instrumentationData: {
          ...state.instrumentationData,
          purchaseProtection: newInstrumentationData,
        },
      }

    case types.INSTRUMENTATION.CLEAR_PURCHASE_PROTECTION:
      return {
        ...state,
        instrumentationData: {
          ...state.instrumentationData,
          purchaseProtection: {
            changedPurchaseProtection: false,
          },
        },
      }

    case types.USER.PROPOSE_PHOTO:
      return {
        ...state,
        proposePhoto: action.data.photo,
      }

    // Phone confirmation flag is passed during recipient details (create token call) in v2
    // Hence moving the flag to user since server reducer mostly deals with data passed on initial load
    case types.USER.TRIGGER_SENDER_PHONE_CONFIRMATION:
      return {
        ...state,
        triggerSenderPhoneConfirmation: true,
      }

    case types.USER.DISABLE_PERSONAL_PAYMENT:
      return {
        ...state,
        isPersonalDisabled: action.data.isSenderPersonalPaymentDisabled,
        personalCipUrl: action.data.personalCipUrl,
      }
    case types.USER.DISABLE_PURCHASE_PAYMENT:
      return {
        ...state,
        isPurchaseDisabled: action.data.isSenderPurchasePaymentDisabled,
        purchaseCipUrl: action.data.purchaseCipUrl,
      }

    case types.USER.REQUIRE_INSTRUMENT_ADDITION:
      return {
        ...state,
        requireSenderInstrumentAddition:
          action.data.requireSenderInstrumentAddition,
      }

    default:
      return state
  }
}
